import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import classnames from 'classnames';
import { route } from 'router';
import { Trans } from 'react-i18next';
import { Button, Input, Text, Link, Checkbox, Collapse } from 'components-guide';
import Info from 'config/info';

import './Subscribe.form.scss';

export interface ValueProps {
    /**
     * name value
     */
    name?: string;
    /**
     * last name value
     */
    lastName?: string;
    /**
     * email value
     */
    email?: string;
    /**
     * privacy value
     */
    privacy?: boolean;
    /**
     * newsletter value
     */
    newsletter?: boolean;
}

export interface SubscribeProps {
    /**
     * Form value
     */
    value?: ValueProps;
    /**
     * Loading
     */
    loading?: boolean;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Style
     */
    style?: any;
    /**
     * Translate prop
     */
    t: any;
    /**
     * Function called on submit form
     */
    onSubmit?(form: any): any;
}

const schema = (t: any) =>
    yup.object().shape({
        name: yup.string().required(t('validation.required', { field: t('forms.subscribe.name.label') })),
        lastName: yup.string().required(t('validation.required', { field: t('forms.subscribe.lastName.label') })),
        email: yup
            .string()
            .email(t('validation.email', { field: t('forms.subscribe.email.label') }))
            .required(t('validation.required', { field: t('forms.subscribe.email.label') })),
        privacy: yup.bool().oneOf([true], t('validation.required', { field: '' })),
        newsletter: yup.bool().oneOf([true], t('validation.required', { field: '' })),
    });

const defaultValue = {
    name: '',
    lastName: '',
    email: '',
    privacy: false,
    newsletter: false,
} as any;

const SubscribeForm: React.FC<SubscribeProps> = ({
    value = defaultValue,
    loading = false,
    className,
    style,
    t,
    onSubmit = () => {},
}) => {
    const { register, handleSubmit, control, formState: { errors } } = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(schema(t)),
        defaultValues: value,
    });
    const [privacityViewMore, setPrivacityViewMore] = useState(false);
    const classNames = classnames(['subscribe-form', className]);

    const handleClickPrivacy = () => window.open(route.app.legal.info(), '_blank');
    const handleOnClickViewMore = () => setPrivacityViewMore(!privacityViewMore);

    return (
        <form className={classNames} style={style} onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form__row">
                <Input
                    className="form-control form__row--item"
                    type="text"
                    id="name"
                    data-cy="name"
                    material
                    label={`${t('forms.subscribe.name.label')} *`}
                    error={errors.name?.['message']}
                    {...register('name')}
                />
                <Input
                    className="form-control form__row--item"
                    type="text"
                    id="lastName"
                    data-cy="lastName"
                    material
                    label={`${t('forms.subscribe.lastName.label')} *`}
                    error={errors.lastName?.['message']}
                    {...register('lastName')}
                />
            </div>
            <Input
                className="form-control"
                type="email"
                id="email"
                data-cy="email"
                material
                label={`${t('forms.subscribe.email.label')} *`}
                error={errors.email?.['message']}
                {...register('email')}
            />
            <div className="mb-1">
                <Controller
                    render={({ field, fieldState }: any) => (
                        <Checkbox
                            {...field}
                            id="privacy"
                            name="privacy"
                            defaultChecked={value.privacy}
                            error={!!errors.privacy?.['message']}
                        >
                            <Text tag="span">
                                <Trans i18nKey="forms.subscribe.privacy.label">
                                    {t('forms.subscribe.privacy.label')}
                                    <Link underline onClick={handleClickPrivacy} />
                                    <Link underline onClick={handleOnClickViewMore} className="link--legal" />
                                    &nbsp;*
                                </Trans>
                            </Text>
                        </Checkbox>
                    )}
                    name="privacy"
                    control={control}
                />
                <Collapse opened={privacityViewMore}>
                    <Text typo="small" className="mt-1">
                        <Trans i18nKey="forms.subscribe.privacy.text">
                            {t('forms.subscribe.privacy.text')}
                            <Link href={`mailto:${Info.emails.mortgages}`} rel="noopener noreferrer" target="_blank" />
                        </Trans>
                    </Text>
                </Collapse>
            </div>
            <div className="mb-2">
                <Controller
                    render={({ field, fieldState }: any) => (
                        <Checkbox
                            {...field}
                            id="newsletter"
                            name="newsletter"
                            defaultChecked={value.newsletter}
                            error={!!errors.newsletter?.['message']}
                        >
                            <Text tag="span">{t('forms.subscribe.newsletter.label')}</Text>
                        </Checkbox>
                    )}
                    name="newsletter"
                    control={control}
                />
            </div>

            <div className="flex row-center">
                <Button block loading={loading} disabled={loading}>
                    {t('forms.subscribe.submit')}
                </Button>
            </div>
        </form>
    );
};

export default SubscribeForm;
